import type { AreaKind } from './areaCatalog';

// Starter checklist — the coverage checklist an inspector walks with (Frozen Spec §6: a
// coverage list, never a photo task list; MUST INSPECT ≠ MUST PHOTOGRAPH). Titles re-express
// the product's established inspection knowledge (old seed route items, 148 rows, read as
// DATA — see docs/KNOWLEDGE-IMPORT-MANIFEST.md K-14); hints are short field prompts written
// for TRUE V2. Long photo-positioning guides of the retired photo-task flow were NOT carried
// over on purpose: the field screen must stay readable at a glance (OWNER-CONTRACT §9.8).
//
// Rows tagged in FIELD_KNOWLEDGE_ANCHORS carry the owner's explicitly preserved rules
// (task book §19) and are pinned by tests/domain/checklistTemplates.test.ts.
export interface ChecklistTemplateRow {
  id: string;
  title: string;
  hint: string;
}

const EXTERIOR: ChecklistTemplateRow[] = [
  { id: 'ext-wall-covering', title: '外墙面层与材料', hint: '记录材料（塑料板 / 木板 / 砖 / 灰泥）；看松动、开裂、水渍，墙板下沿离地距离。' },
  { id: 'ext-foundation-visible', title: '可见地基与墙根', hint: '沿墙根看露出的地基：裂缝、剥落，泥土或灌木是否贴墙。近拍：墙根抹灰层的裂缝与脱落处。' },
  { id: 'ext-grading', title: '地面排水坡度', hint: '房屋四周地面是往外走还是往房子倒坡；只记录水往哪边流。' },
  { id: 'ext-gutters', title: '天沟与落水管', hint: '天沟有无堵塞、下垂、脱开；落水管出口是否把水引离地基（延长管 / 溅水板）。近拍：每根落水管出口、脱开的接口和水落下的位置。' },
  { id: 'ext-windows-doors', title: '外部门窗与过梁', hint: '代表性抽查：框缝密封、过梁开裂发黑、木框腐朽。异常的才单独近拍；外门关上后近拍门边、门底的缝隙。' },
  { id: 'ext-entry-steps', title: '入口台阶与扶手', hint: '约 4 级以上踏步通常应有扶手；台阶稳固、踏步一致、扶手是否松动。近拍：扶手高度与两端收头、木台阶接土处。' },
  { id: 'ext-deck-patio', title: '露台 / 平台 / 阳台', hint: '护栏高度与间隙、与房屋连接方式（托梁挂件 / 螺栓）、木材腐朽、支柱基础。近拍：台面板、立柱底部、木料接土处。' },
  { id: 'ext-driveway', title: '车道、人行道与铺装', hint: '沉降、开裂、高低差绊脚点，铺装是否朝房子倒坡。' },
  { id: 'ext-gfci', title: '室外插座漏电保护与防雨盖', hint: '先确认插座本身是不是 GFCI（有 test / reset 键）。不是的话保护可能在上游，用测试仪实测再下结论；防雨盖是否完好。' },
  { id: 'ext-hose-bibb', title: '室外水龙头', hint: '开水试出水与关闭，看渗漏、是否防冻型、有无防倒流装置。' },
  { id: 'ext-neighbor-influence', title: '挡土墙（本物业或相邻）/ 大树 / 邻宅排水', hint: '挡土墙倾斜或外鼓、树冠树根贴近地基、邻居的水流向本房——只记录对本物业的可见影响，不判责任。' },
  { id: 'ext-wdi', title: '木材破坏性昆虫迹象（泥管 / 掉翅 / 注药钻孔）', hint: '沿墙根与地坪找白蚁泥管、木粉、成排的疑似历史注药钻孔。有活动或历史侵害迹象，建议由有资质的白蚁 / 害虫防治公司评估。' }
];

const ROOF: ChecklistTemplateRow[] = [
  { id: 'roof-covering', title: '屋面材料与整体状况', hint: '记录材料与层数；缺失、翘曲、颗粒脱落、苔藓、补丁。' },
  { id: 'roof-method', title: '观察方式与看不到的坡面', hint: '上屋顶 / 梯子边缘 / 地面变焦 / 无人机；看不到的坡面写进检查限制。' },
  { id: 'roof-flashing', title: '泛水与穿屋面管件', hint: '烟囱、管道、天窗、屋面碰墙处：泛水翘起、锈蚀、只靠补胶。' },
  { id: 'roof-valleys-edges', title: '谷线、屋檐、檐板与滴水板', hint: '谷线积叶、屋檐切边、檐板（soffit）完好与通风口、滴水板有无、檐口板腐朽。近拍：檐板与墙、檐口板之间的缝隙或破洞。' },
  { id: 'roof-chimney', title: '烟囱（如有）', hint: '砖缝风化、顶盖开裂、烟囱帽 / 防雨罩、与屋面交接泛水。' },
  { id: 'roof-ventilation', title: '屋顶通风体系（进风 + 出风）', hint: '通风是进风（檐口 / 屋檐板）和出风（屋脊 / 盒式 / 风帽 / 山墙）一起算；只看到山墙通风口不算完整，确认进风口没有被保温层堵住。' },
  { id: 'roof-skylight', title: '天窗（如有）', hint: '周边泛水、玻璃雾化、室内侧水迹。' },
  { id: 'roof-gutter-attachment', title: '天沟固定与屋檐交接', hint: '天沟托架松脱、坡向不对、与屋檐交接漏水痕迹。' }
];

const GARAGE: ChecklistTemplateRow[] = [
  { id: 'garage-door-reverse', title: '车库门与开门机安全反转', hint: '手挡或压力测试自动反转；光电眼高度与对位；门弹簧、导轨、缆绳。' },
  { id: 'garage-separation', title: '与居住区的防火分隔', hint: '通往住宅的门应为自闭的实心 / 防火门；共用墙与顶棚无破洞。' },
  { id: 'garage-floor', title: '车库地面与排水', hint: '裂缝、沉降、地面是否向门口倾斜，油渍与积水。' },
  { id: 'garage-gfci', title: '车库插座漏电保护', hint: '先确认是不是 GFCI；不是的话用测试仪确认上游保护，写清插座位置。' },
  { id: 'garage-structure', title: '车库墙体与屋顶结构', hint: '墙板、屋架可见状况，渗漏痕迹。' },
  { id: 'garage-appliances', title: '车库内设备（热水器 / 炉具，如有）', hint: '点火源离地高度、燃气设备防撞保护、排气。' }
];

const BASEMENT: ChecklistTemplateRow[] = [
  { id: 'bsmt-access-finish', title: '可进入情况与装修程度', hint: '已装修 / 部分装修 / 未装修；装修覆盖造成结构不可见时，记录检查限制，不机械要求补拍。' },
  { id: 'bsmt-foundation-walls', title: '地基墙可见状况', hint: '裂缝类型（竖向 / 水平 / 阶梯）、外鼓、位移、渗水痕迹。' },
  { id: 'bsmt-moisture', title: '潮气、水迹与渗水', hint: '水线、白色盐霜、霉味、除湿机、墙脚潮湿或存放物垫高。' },
  { id: 'bsmt-structure-alignment', title: '内部墙 / 梁 / 柱倾斜变形', hint: '蹲在柱底顺着往上看：柱与地面、柱顶与梁底是否仍垂直对位；承重墙有无整体外倾或中段鼓出。' },
  { id: 'bsmt-sump', title: '污水泵 / 排水泵（如有）', hint: '泵是否运作、有盖、排放去向、止回阀。' },
  { id: 'bsmt-stairs', title: '楼梯、扶手与净高', hint: '扶手、护栏间隙、踏步一致、撞头高度。近拍：扶手高度与两端收头、栏杆间距、楼梯敞开的一侧。' },
  { id: 'bsmt-gfci', title: '地下室插座漏电保护', hint: '未装修地下室通常应有 GFCI；先确认对象是不是 GFCI，用测试仪确认。' },
  { id: 'bsmt-pests', title: '虫害迹象', hint: '泥管、木粉、木材与土壤接触；只记录可见迹象，不做白蚁检查（NPMA-33 是另一项服务）。' },
  { id: 'bsmt-mold', title: '霉变迹象', hint: '霉斑、疑似霉菌样物质、霉味；只记录看到的，不鉴定、不判健康结论。' },
  { id: 'bsmt-alarm', title: '地下室层烟感 / CO 报警器', hint: '本层有无烟感；燃气设备附近有无一氧化碳报警器。' },
  { id: 'bsmt-outside-review', title: '对照室外发现复核（挡土墙 / 负坡对应侧）', hint: '室外见到挡土墙倾斜或地面向房子倒坡时，进来看同一侧的墙脚潮气与结构对位。' },
  { id: 'bsmt-crawlspace', title: '爬行空间（如有）', hint: '进入方式、地面防潮膜、通风口、木材接触土壤、管线保温。' }
];

const STRUCTURE: ChecklistTemplateRow[] = [
  { id: 'str-main-beam', title: '主梁与支撑柱', hint: '挠曲、开裂、临时支撑（千斤顶柱）、拼接处。' },
  { id: 'str-post-base', title: '支撑柱底部', hint: '锈蚀、腐朽、无基础垫、直接立在土上。' },
  { id: 'str-joists', title: '楼板梁与开孔切口', hint: '过大的钻孔 / 切口、下垂、火烧痕、改动过的托梁。' },
  { id: 'str-connections', title: '连接节点与紧固件', hint: '托梁挂件、螺栓、钉板是否齐全，有无锈蚀。' },
  { id: 'str-bearing-walls', title: '承重柱与内承重墙倾斜位移', hint: '从一端顺着墙面看整体倾斜或中段鼓出；柱与梁底几何对位。' },
  { id: 'str-exterior-signs', title: '外部结构迹象', hint: '阶梯裂缝、门窗歪斜卡顿、砖缝错位、墙体外倾。' },
  { id: 'str-limitations', title: '可观察范围与限制', hint: '装修 / 保温 / 杂物遮挡的结构写进限制。' }
];

const ELECTRICAL: ChecklistTemplateRow[] = [
  { id: 'elec-service-entrance', title: '电力引入与电表', hint: '进户线头、滴水弯、进户线离地 / 离屋面间距，电缆是否走在墙板下。' },
  { id: 'elec-panel-overview', title: '配电箱位置、前方净空与角色', hint: '前方操作空间、盖板在位、主箱还是分箱。' },
  { id: 'elec-panel-interior', title: '拆盖后内部（安全前提下）', hint: '单端子双线、断路器与线径、接地搭接、烧焦锈蚀、敞开的开孔。' },
  { id: 'elec-panel-label', title: '品牌 / 容量标签', hint: '记录品牌与安培数；老式品牌只建议评估，不下定论。' },
  { id: 'elec-grounding', title: '接地与搭接', hint: '接地极导线、水管搭接跨接线是否可见。' },
  { id: 'elec-wiring-type', title: '可见布线类型', hint: '铝线、瓷管布线、外露接头、缺盖接线盒。' },
  { id: 'elec-gfci-afci', title: 'GFCI / AFCI 保护范围', hint: '先确认对象是否 GFCI。需要 GFCI 的位置（厨房台面 / 卫生间 / 车库 / 室外 / 洗衣 / 未装修地下室）若插座不是 GFCI，用测试仪确认上游保护，写清位置。' },
  { id: 'elec-outlets-sample', title: '插座与开关抽查', hint: '测试仪抽测极性与接地；异常才单独记录。' },
  { id: 'elec-fixtures', title: '灯具与吊扇抽查', hint: '开关对应、松动、外露灯头。' }
];

const PLUMBING: ChecklistTemplateRow[] = [
  { id: 'plb-main-shutoff', title: '主进水与总阀', hint: '位置、材质（铅 / 镀锌 / 铜 / 塑料）、总阀可操作。' },
  { id: 'plb-supply-piping', title: '可见供水管材与渗漏', hint: '铜 / PEX / 镀锌 / 铅；绿锈、渗漏、混接。' },
  { id: 'plb-drain-piping', title: '可见排水管与通气', hint: '铸铁 / PVC / ABS；渗漏、S 弯、通气管。' },
  { id: 'plb-water-pressure', title: '水压与流量', hint: '多个龙头同时开时的流量下降。' },
  { id: 'plb-fixtures-sample', title: '龙头 / 排水抽查', hint: '各用水点出水、排水速度、止水阀。' },
  { id: 'plb-water-meter', title: '水表（如有）', hint: '位置与可读状态；无表也是一个答案。' },
  { id: 'plb-sewer-cleanout', title: '主排污清理口与排污去向', hint: '市政 / 化粪池；清理口位置与盖帽。' },
  { id: 'plb-cross-connection', title: '交叉连接 / 防倒流', hint: '洗手盆下、软管龙头真空破坏器、锅炉补水。' }
];

const WATER_HEATER: ChecklistTemplateRow[] = [
  { id: 'wh-overview', title: '热水器整体、类型与位置', hint: '燃气 / 电 / 无水箱；离地、可进入、周围可燃物。' },
  { id: 'wh-data-plate', title: '铭牌 / 年份 / 容量', hint: '记录品牌、序列号年份、容量。' },
  { id: 'wh-tpr', title: 'TPR 泄压阀与排放管', hint: '安全组件逐项看：阀在、排放管向下、不缩径、末端离地约 15 cm、无堵头、无软管代替。' },
  { id: 'wh-venting', title: '排气 / 烟道（燃气型）', hint: '连接件坡度向上、无脱开、锈穿；单壁管离可燃物间距。' },
  { id: 'wh-combustion-air', title: '燃烧进气与间距（燃气型）', hint: '封闭空间有无进气口；储物贴近设备。' },
  { id: 'wh-connections', title: '进出水阀、燃气阀与接管', hint: '阀门在、软管状态、膨胀罐（如适用）。' },
  { id: 'wh-base', title: '底部渗漏、锈蚀与接水盘', hint: '底部锈蚀、渗漏痕迹、接水盘及其排水。' },
  { id: 'wh-temperature', title: '出水温度观察', hint: '出水过烫的烫伤风险；仅记录观察。' }
];

const HVAC: ChecklistTemplateRow[] = [
  { id: 'hvac-heating-unit', title: '供暖设备类型与整体', hint: '炉 / 锅炉 / 热泵；铭牌年份、燃料。' },
  { id: 'hvac-operation', title: '运行观察', hint: '用温控器启动，观察点火、出风 / 出水、异响。' },
  { id: 'hvac-venting', title: '排气 / 烟道连接', hint: '脱开、锈蚀、坡度、与热水器共用烟道。' },
  { id: 'hvac-filter', title: '过滤器', hint: '有无、脏污、尺寸与方向。' },
  { id: 'hvac-clearance', title: '设备周围间距与可燃物', hint: '储物贴近设备、维修通道。' },
  { id: 'hvac-condensate', title: '冷凝水排放（高效炉 / 空调）', hint: '排放去向、积水、冷凝泵运作。' },
  { id: 'hvac-distribution', title: '风管 / 暖气片 / 分配', hint: '代表性检查：各房间有出风或散热；明显脱开的风管。近拍：各房间的出风口或暖气片（锈蚀、渗漏、被挡住）。' },
  { id: 'hvac-ac-unit', title: '空调室外机与基础', hint: '水平、间距、线路保护、机龄。' },
  { id: 'hvac-refrigerant', title: '冷媒管与保温', hint: '保温破损、穿墙封堵。' },
  { id: 'hvac-boiler-relief', title: '锅炉泄压阀与排放管（锅炉才有）', hint: '整定压力可读、排放管向下、末端位置。' },
  { id: 'hvac-oil-tank', title: '燃油油罐（燃油供暖才有）', hint: '锈蚀、渗漏、支脚、油位表、加油口。' }
];

const ATTIC: ChecklistTemplateRow[] = [
  { id: 'attic-access', title: '进入方式与可观察范围', hint: '入口位置、是否进入或仅入口观察；限制写清。' },
  { id: 'attic-sheathing', title: '屋面板下侧与水迹', hint: '手电斜打光看水迹、发黑、霉、钉头锈。' },
  { id: 'attic-insulation', title: '保温层类型与覆盖', hint: '类型、大致厚度、缺口、被压实。' },
  { id: 'attic-ventilation', title: '通风口（进风 + 出风）', hint: '檐口进风口是否被保温层堵住；屋脊 / 山墙出风；只有山墙口不算完整。' },
  { id: 'attic-exhaust-termination', title: '排气管终点', hint: '浴室 / 烘干机排风是否直接排到室外，不是悬在阁楼里。' },
  { id: 'attic-structure', title: '椽子 / 桁架状况', hint: '开裂、被改动的桁架、下垂、临时支撑。' },
  { id: 'attic-wiring', title: '可见电线与接线盒', hint: '瓷管布线、外露接头、被保温层覆盖的嵌灯。' },
  { id: 'attic-pests', title: '动物 / 虫害痕迹', hint: '粪便、巢、啃咬痕迹、被踩塌的保温层。' }
];

const INTERIOR: ChecklistTemplateRow[] = [
  { id: 'int-limitations', title: '家具 / 杂物遮挡与可进入情况', hint: '看不到的墙面、地面、壁橱写进限制。' },
  { id: 'int-surfaces', title: '墙、顶、地状况', hint: '水迹、开裂、地面不平；代表性检查，异常才近拍。' },
  { id: 'int-windows', title: '门窗开合与密封', hint: '抽查开关、锁扣、玻璃雾化、密封条。' },
  { id: 'int-doors', title: '室内门', hint: '开合、闩锁、自行摆动（可能提示地面不平）。近拍：门关上后与门框之间的缝隙。' },
  { id: 'int-outlets', title: '插座与开关抽查', hint: '测试仪抽测；缺盖板、松动。' },
  { id: 'int-stairs', title: '楼梯与扶手（本层如有）', hint: '扶手、护栏间隙、踏步一致、净高。近拍：扶手高度与两端收头、栏杆间距、楼梯敞开的一侧。' },
  { id: 'int-fireplace', title: '壁炉 / 柴炉（如有）', hint: '挡板、烟道可见部分、砖缝、炉前地面。' },
  { id: 'int-alarms', title: '烟雾 / 一氧化碳报警器（本层）', hint: '本层与卧室外走廊有无烟感；有无 CO 报警器。' }
];

const KITCHEN: ChecklistTemplateRow[] = [
  { id: 'kit-sink', title: '水槽出水 / 排水 / 下柜渗漏', hint: '开热冷水、看排水速度，下柜水渍与软管。' },
  { id: 'kit-gfci', title: '台面插座漏电保护', hint: '先看是不是 GFCI；不是的话用测试仪确认上游保护。' },
  { id: 'kit-exhaust-ducting', title: '厨房排风管道去向（可见时）', hint: '有排风管时看它是否通到室外；循环式（无外排管）只记录，不是缺陷；本行不评估抽油烟机本体（风机 / 灯 / 滤网），本体问题记「其他发现」。' },
  { id: 'kit-cabinets-counters', title: '橱柜门 / 抽屉与台面', hint: '开合、松脱、台面开裂与接缝。' },
  { id: 'kit-gas', title: '燃气灶连接与关断阀（如有）', hint: '软管状态、关断阀可及、气味。' },
  { id: 'kit-surfaces', title: '墙、顶、地与窗', hint: '水迹、地面不平、窗开合。' }
];

const BATHROOM: ChecklistTemplateRow[] = [
  { id: 'bath-tub-shower', title: '浴缸 / 淋浴防水密封', hint: '瓷砖缝、硅胶、门框、排水速度；花洒与龙头。' },
  { id: 'bath-toilet', title: '马桶稳固与底部', hint: '摇晃、底部渗漏、水箱运作。' },
  { id: 'bath-sink', title: '洗手盆 / 台面 / 下柜', hint: '出水、排水、止水阀、下柜渗漏。' },
  { id: 'bath-exhaust', title: '排风 / 通风', hint: '排风扇或可开窗；排风去向是否室外。' },
  { id: 'bath-gfci', title: '漏电保护插座', hint: '先确认是不是 GFCI，用测试仪确认；写清位置。' },
  { id: 'bath-surfaces', title: '墙、顶、地与水迹', hint: '地面松软、天花水迹、通风不良的霉。' },
  { id: 'bath-water-flow', title: '同时用水的水压变化', hint: '开淋浴再冲马桶，看流量变化。' }
];

const BEDROOM: ChecklistTemplateRow[] = [
  { id: 'bed-egress', title: '卧室窗户开启（补充观察）', hint: '试开一扇窗：能否打开、有无卡死或损坏；地下室卧室同样记录；不判定逃生尺寸是否合规。近拍：打开后的整扇窗与锁扣；地下室卧室连窗井一起拍。' },
  { id: 'bed-windows-doors', title: '门窗开合与密封', hint: '抽查开关、锁扣、密封。' },
  { id: 'bed-outlets', title: '插座抽查', hint: '测试仪抽测；缺盖板、松动。' },
  { id: 'bed-surfaces', title: '墙、顶、地状况', hint: '水迹、开裂、地面不平。' },
  { id: 'bed-alarm', title: '卧室内烟感', hint: '每间卧室内应有烟感；记录缺失位置。' },
  { id: 'bed-closet', title: '壁橱与灯具', hint: '壁橱内裸灯泡靠近储物、门开合。' }
];

const SAFETY: ChecklistTemplateRow[] = [
  { id: 'safe-alarms-system', title: '全屋烟感与 CO 报警器（系统级复核）', hint: '逐层复核：每层、每间卧室内、卧室外走廊有烟感；燃气设备附近与带车库处有 CO 报警器。缺的位置比有的位置更重要。' },
  { id: 'safe-handrails', title: '楼梯扶手与护栏（全屋复核）', hint: '约 4 级以上应有扶手；护栏高度与间隙。' },
  { id: 'safe-garage-door', title: '车库门自动反转（复核）', hint: '与车库区域一致；未测试要写明。' },
  { id: 'safe-tpr', title: 'TPR 排放管（复核）', hint: '与热水器区域一致。' },
  { id: 'safe-egress', title: '卧室窗户开启（复核 · 补充观察）', hint: '每间卧室至少试开一扇窗；只记录开启情况。' },
  { id: 'safe-electrical-hazards', title: '明显电气隐患汇总', hint: '裸露接头、缺盖板、烧焦、延长线当固定布线。' },
  { id: 'safe-trip-fall', title: '绊倒 / 坠落隐患', hint: '铺装高低差、松动台阶、缺护栏的落差。' },
  { id: 'safe-wdi', title: '白蚁 / 木材害虫迹象汇总', hint: '有活动或历史侵害迹象时，建议对象必须是有资质的白蚁 / 害虫防治公司。' }
];

const OTHER: ChecklistTemplateRow[] = [
  { id: 'other-condition', title: '区域整体状况', hint: '写清这是什么区域、看到了什么。' }
];

export const CHECKLIST_TEMPLATES: Record<AreaKind, readonly ChecklistTemplateRow[]> = {
  exterior: EXTERIOR,
  roof: ROOF,
  garage: GARAGE,
  basement: BASEMENT,
  structure: STRUCTURE,
  electrical: ELECTRICAL,
  plumbing: PLUMBING,
  waterHeater: WATER_HEATER,
  hvac: HVAC,
  attic: ATTIC,
  interior: INTERIOR,
  kitchen: KITCHEN,
  bathroom: BATHROOM,
  bedroom: BEDROOM,
  safety: SAFETY,
  other: OTHER
};

export function templateRowsFor(kind: AreaKind): ChecklistTemplateRow[] {
  return CHECKLIST_TEMPLATES[kind].map((row) => ({ ...row }));
}

export function templateRowCount(): number {
  return Object.values(CHECKLIST_TEMPLATES).reduce((n, rows) => n + rows.length, 0);
}

// NY F1 Round 2 (frozen plan §13; NY-SCOPE-AUTHORITY-2026-09-06-F1 CK-02 / CK-03 / CK-07) and
// Round 5 (Owner ruling OD-6 → CK-09): the CONDITIONAL rows. They are NOT part of the starter
// checklist — the store seeds one into an existing area of its kind only when the trigger fact is
// recorded (src/domain/descriptors.ts conditionalRowsDue), once, at the end of the area. Ids are
// stable and referenced by stored records exactly like every other row.
//
// CK-09 (2026-09-09): the well and septic rows are OBSERVATION-ONLY and appear only when the water
// supply / waste disposal descriptor says the property is on its own system (197-5.8(c)(9),(12): the
// systems themselves are not required to be evaluated). A municipal house never sees the rows.
export interface ConditionalTemplateRow extends ChecklistTemplateRow {
  kind: AreaKind;
}

export const CONDITIONAL_TEMPLATE_ROWS: readonly ConditionalTemplateRow[] = Object.freeze([
  { kind: 'plumbing', id: 'plb-fuel-shutoff', title: '燃料储存 / 输送（可见部分）与总燃料关断阀位置', hint: '燃气 / 燃油 / 丙烷：可见管线与储罐、总关断阀在哪里、是否可操作。' },
  { kind: 'hvac', id: 'hvac-supplementary-heat', title: '固定式辅助供暖设备（如有）', hint: '踢脚线电暖、壁挂式暖器、固定式电暖：位置、运行观察、周围可燃物。' },
  { kind: 'basement', id: 'bsmt-foundation-insulation', title: '地基区域保温与通风（未装修部分）', hint: '地基墙 / 楼板下保温类型与覆盖、通风口与空气流通；只记录看得到的。' },
  { kind: 'exterior', id: 'ext-well', title: '水井设备与地面迹象（供水为自有时）', hint: '只看可见的井口、井盖与压力罐及周边地面迹象；不评估水井系统、出水量或水质。' },
  { kind: 'exterior', id: 'ext-septic', title: '化粪池地面迹象（如为化粪池系统）', hint: '地面异常湿润、异味、盖板位置；市政排污则不适用。' }
]);

// Rows that WERE in the starter checklist and are not seeded any more. They stay resolvable so a
// record written while they were seeded still reads, prints and translates exactly as before —
// nothing an inspector already recorded is lost or renamed by a scope decision.
//   kit-appliances  CK-W6 (Owner ruling OD-6, 2026-09-09): household appliances are not required by
//                   197-5.12(b)(7); an inspector who chooses to look records it under Other Findings
//                   as a voluntary observation (OD-13), not as a checklist row.
//   kit-range-vent  2026-09-13 (TW-4, ratification §5.2 / §6): a MIXED row — the range hood appliance (fan,
//                   light) and the kitchen exhaust ducting in one line. Split: the ducting stays as
//                   `kit-exhaust-ducting` (NY_REQUIRED, 197-5.12(a)(4)); the appliance leaves the checklist for
//                   the OD-13 voluntary-observation path. A recirculating range hood is never a defect.
//   bsmt-pests-mold 2026-09-13 (ratification §5.2 / §7): split into `bsmt-pests` (WDI evidence) and
//                   `bsmt-mold` (mold-like substance) — different authority, referral and wording.
export const RETIRED_TEMPLATE_ROWS: readonly ConditionalTemplateRow[] = Object.freeze([
  { kind: 'kitchen', id: 'kit-appliances', title: '主要电器抽查', hint: '灶具、烤箱、洗碗机运行；防倾倒支架。' },
  { kind: 'kitchen', id: 'kit-range-vent', title: '抽油烟 / 排风', hint: '排到室外还是循环式；运作与灯。' },
  { kind: 'basement', id: 'bsmt-pests-mold', title: '虫害 / 霉变迹象', hint: '泥管、木粉、霉斑，木材与土壤接触。' }
]);

export function conditionalTemplateRowById(id: string): ConditionalTemplateRow | null {
  return CONDITIONAL_TEMPLATE_ROWS.find((row) => row.id === id) ?? null;
}

const ROWS_BY_ID: ReadonlyMap<string, ChecklistTemplateRow> = new Map([
  ...Object.values(CHECKLIST_TEMPLATES).flatMap((rows) => rows.map((row) => [row.id, row] as const)),
  ...[...CONDITIONAL_TEMPLATE_ROWS, ...RETIRED_TEMPLATE_ROWS].map((row) => [row.id, { id: row.id, title: row.title, hint: row.hint }] as const)
]);

// The zh-CN template row behind a checklist item (V2ChecklistItem.templateId), or null.
export function templateRowById(id: string): ChecklistTemplateRow | null {
  return ROWS_BY_ID.get(id) ?? null;
}

// A checklist item stores the zh-CN hint of its row at creation. When a row's hint is sharpened, the
// texts it had before are listed here, so an item created earlier is still recognised as carrying the
// template hint: the display (src/i18n/labels.ts itemHintFor) then shows the CURRENT hint in either App
// Language instead of falling back to the stored Chinese. Only hints change here — never ids or titles.
//   2026-10-27 (AI overnight review: the five finished reports' recurring misses were defects at the edge
//   of distant photos): a one-line 「近拍：…」 close-up prompt on the rows that cover them.
export const PREVIOUS_TEMPLATE_HINTS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  'ext-foundation-visible': ['沿墙根看露出的地基：裂缝、剥落，泥土或灌木是否贴墙。'],
  'ext-gutters': ['天沟有无堵塞、下垂、脱开；落水管出口是否把水引离地基（延长管 / 溅水板）。'],
  'ext-windows-doors': ['代表性抽查：框缝密封、过梁开裂发黑、木框腐朽。异常的才单独近拍。'],
  'ext-entry-steps': ['约 4 级以上踏步通常应有扶手；台阶稳固、踏步一致、扶手是否松动。'],
  'ext-deck-patio': ['护栏高度与间隙、与房屋连接方式（托梁挂件 / 螺栓）、木材腐朽、支柱基础。'],
  'roof-valleys-edges': ['谷线积叶、屋檐切边、檐板（soffit）完好与通风口、滴水板有无、檐口板腐朽。'],
  'bsmt-stairs': ['扶手、护栏间隙、踏步一致、撞头高度。'],
  'hvac-distribution': ['代表性检查：各房间有出风或散热；明显脱开的风管。'],
  'int-doors': ['开合、闩锁、自行摆动（可能提示地面不平）。'],
  'int-stairs': ['扶手、护栏间隙、踏步一致、净高。'],
  'bed-egress': ['试开一扇窗：能否打开、有无卡死或损坏；地下室卧室同样记录；不判定逃生尺寸是否合规。']
});

// The stored hint is the template's own text — the current one or one it had before.
export function isTemplateHint(templateId: string, hint: string): boolean {
  const row = templateRowById(templateId);
  if (!row) return false;
  return row.hint === hint || (PREVIOUS_TEMPLATE_HINTS[templateId] ?? []).includes(hint);
}

// Owner-preserved field rules (task book §19) → the template rows that carry them.
// Each anchor lists phrases the row text must keep so the rule cannot be edited away silently.
export interface FieldKnowledgeAnchor {
  rule: string;
  kind: AreaKind;
  templateId: string;
  mustMention: readonly string[];
}

export const FIELD_KNOWLEDGE_ANCHORS: readonly FieldKnowledgeAnchor[] = Object.freeze([
  { rule: 'GFCI：先确认对象是不是 GFCI', kind: 'electrical', templateId: 'elec-gfci-afci', mustMention: ['是否 GFCI', '测试仪'] },
  { rule: '非 GFCI 的需要位置要明确处理', kind: 'kitchen', templateId: 'kit-gfci', mustMention: ['GFCI', '测试仪'] },
  { rule: '热水器安全组件拆开检查', kind: 'waterHeater', templateId: 'wh-tpr', mustMention: ['TPR', '不缩径', '无堵头'] },
  { rule: '屋顶通风不能只看山墙通风', kind: 'roof', templateId: 'roof-ventilation', mustMention: ['进风', '出风', '山墙'] },
  { rule: '地面排水坡度', kind: 'exterior', templateId: 'ext-grading', mustMention: ['倒坡', '水往哪边流'] },
  { rule: '地下室内部墙 / 梁柱倾斜变形', kind: 'basement', templateId: 'bsmt-structure-alignment', mustMention: ['倾斜', '梁'] },
  { rule: '烟雾 / CO 系统级检查', kind: 'safety', templateId: 'safe-alarms-system', mustMention: ['每间卧室', 'CO'] },
  { rule: '相邻挡土墙 / 树根影响记录', kind: 'exterior', templateId: 'ext-neighbor-influence', mustMention: ['挡土墙', '树根'] },
  { rule: '疑似历史白蚁注药钻孔', kind: 'exterior', templateId: 'ext-wdi', mustMention: ['注药钻孔'] },
  { rule: '白蚁建议对象 = 有资质的白蚁/害虫防治公司', kind: 'safety', templateId: 'safe-wdi', mustMention: ['有资质的白蚁 / 害虫防治公司'] }
]);

// ── Scope classification (Owner ratification 2026-09-13, TW-2 · ratification §5) ──────────────────
// Every template row carries exactly one scope class. The MODEL is frozen; the counts are not.
//   nyRequired        19 NYCRR 197-5.4 … 197-5.15 shall observe / operate / describe / report
//   supplemental      voluntary / beyond-minimum observation over a not-required or excluded subject
//                     (197-5.2(b); never presented as statutory scope — UI badge + report suffix)
//   methodLimitation  observation-method / limitation / descriptor rows (197-5.3(c), 197-5.7(b),
//                     197-5.15(a)(1), 197-5.8(d))
// Audit at the ratification: BEFORE 104 / 12 / 8 = 124 → AFTER 104 / 13 / 8 = 125 (kit-range-vent split,
// bsmt-pests-mold split). Retired and conditional ids are classified too so stored records print right.
export type RowScope = 'nyRequired' | 'supplemental' | 'methodLimitation';

export const TEMPLATE_ROW_SCOPE: Readonly<Record<string, RowScope>> = Object.freeze({
  'ext-wall-covering': 'nyRequired',
  'ext-foundation-visible': 'nyRequired',
  'ext-grading': 'nyRequired',
  'ext-gutters': 'nyRequired',
  'ext-windows-doors': 'nyRequired',
  'ext-entry-steps': 'nyRequired',
  'ext-deck-patio': 'nyRequired',
  'ext-driveway': 'nyRequired',
  'ext-gfci': 'nyRequired',
  'ext-hose-bibb': 'supplemental',
  'ext-neighbor-influence': 'nyRequired',
  'ext-wdi': 'supplemental',
  'roof-covering': 'nyRequired',
  'roof-method': 'methodLimitation',
  'roof-flashing': 'nyRequired',
  'roof-valleys-edges': 'nyRequired',
  'roof-chimney': 'nyRequired',
  'roof-ventilation': 'nyRequired',
  'roof-skylight': 'nyRequired',
  'roof-gutter-attachment': 'nyRequired',
  'garage-door-reverse': 'nyRequired',
  'garage-separation': 'supplemental',
  'garage-floor': 'nyRequired',
  'garage-gfci': 'nyRequired',
  'garage-structure': 'nyRequired',
  'garage-appliances': 'nyRequired',
  'bsmt-access-finish': 'methodLimitation',
  'bsmt-foundation-walls': 'nyRequired',
  'bsmt-moisture': 'nyRequired',
  'bsmt-structure-alignment': 'nyRequired',
  'bsmt-sump': 'nyRequired',
  'bsmt-stairs': 'nyRequired',
  'bsmt-gfci': 'nyRequired',
  'bsmt-pests': 'supplemental',
  'bsmt-mold': 'supplemental',
  'bsmt-alarm': 'nyRequired',
  'bsmt-outside-review': 'nyRequired',
  'bsmt-crawlspace': 'nyRequired',
  'str-main-beam': 'nyRequired',
  'str-post-base': 'nyRequired',
  'str-joists': 'nyRequired',
  'str-connections': 'nyRequired',
  'str-bearing-walls': 'nyRequired',
  'str-exterior-signs': 'nyRequired',
  'str-limitations': 'methodLimitation',
  'elec-service-entrance': 'nyRequired',
  'elec-panel-overview': 'nyRequired',
  'elec-panel-interior': 'nyRequired',
  'elec-panel-label': 'nyRequired',
  'elec-grounding': 'nyRequired',
  'elec-wiring-type': 'nyRequired',
  'elec-gfci-afci': 'nyRequired',
  'elec-outlets-sample': 'nyRequired',
  'elec-fixtures': 'nyRequired',
  'plb-main-shutoff': 'nyRequired',
  'plb-supply-piping': 'nyRequired',
  'plb-drain-piping': 'nyRequired',
  'plb-water-pressure': 'nyRequired',
  'plb-fixtures-sample': 'nyRequired',
  'plb-water-meter': 'methodLimitation',
  'plb-sewer-cleanout': 'nyRequired',
  'plb-cross-connection': 'nyRequired',
  'wh-overview': 'nyRequired',
  'wh-data-plate': 'methodLimitation',
  'wh-tpr': 'nyRequired',
  'wh-venting': 'nyRequired',
  'wh-combustion-air': 'supplemental',
  'wh-connections': 'nyRequired',
  'wh-base': 'nyRequired',
  'wh-temperature': 'supplemental',
  'hvac-heating-unit': 'nyRequired',
  'hvac-operation': 'nyRequired',
  'hvac-venting': 'nyRequired',
  'hvac-filter': 'nyRequired',
  'hvac-clearance': 'supplemental',
  'hvac-condensate': 'nyRequired',
  'hvac-distribution': 'nyRequired',
  'hvac-ac-unit': 'nyRequired',
  'hvac-refrigerant': 'nyRequired',
  'hvac-boiler-relief': 'nyRequired',
  'hvac-oil-tank': 'nyRequired',
  'attic-access': 'methodLimitation',
  'attic-sheathing': 'nyRequired',
  'attic-insulation': 'nyRequired',
  'attic-ventilation': 'nyRequired',
  'attic-exhaust-termination': 'nyRequired',
  'attic-structure': 'nyRequired',
  'attic-wiring': 'nyRequired',
  'attic-pests': 'supplemental',
  'int-limitations': 'methodLimitation',
  'int-surfaces': 'nyRequired',
  'int-windows': 'nyRequired',
  'int-doors': 'nyRequired',
  'int-outlets': 'nyRequired',
  'int-stairs': 'nyRequired',
  'int-fireplace': 'nyRequired',
  'int-alarms': 'nyRequired',
  'kit-sink': 'nyRequired',
  'kit-gfci': 'nyRequired',
  'kit-exhaust-ducting': 'nyRequired',
  'kit-cabinets-counters': 'supplemental',
  'kit-gas': 'nyRequired',
  'kit-surfaces': 'nyRequired',
  'bath-tub-shower': 'nyRequired',
  'bath-toilet': 'nyRequired',
  'bath-sink': 'nyRequired',
  'bath-exhaust': 'nyRequired',
  'bath-gfci': 'nyRequired',
  'bath-surfaces': 'nyRequired',
  'bath-water-flow': 'nyRequired',
  'bed-egress': 'supplemental',
  'bed-windows-doors': 'nyRequired',
  'bed-outlets': 'nyRequired',
  'bed-surfaces': 'nyRequired',
  'bed-alarm': 'nyRequired',
  'bed-closet': 'nyRequired',
  'safe-alarms-system': 'nyRequired',
  'safe-handrails': 'nyRequired',
  'safe-garage-door': 'nyRequired',
  'safe-tpr': 'nyRequired',
  'safe-egress': 'supplemental',
  'safe-electrical-hazards': 'nyRequired',
  'safe-trip-fall': 'nyRequired',
  'safe-wdi': 'supplemental',
  'other-condition': 'methodLimitation',
  'plb-fuel-shutoff': 'nyRequired',
  'hvac-supplementary-heat': 'nyRequired',
  'bsmt-foundation-insulation': 'nyRequired',
  'ext-well': 'supplemental',
  'ext-septic': 'supplemental',
  'kit-appliances': 'supplemental',
  'kit-range-vent': 'nyRequired',
  'bsmt-pests-mold': 'supplemental'
});

// The scope class of a template row; null for an inspector-added row (no template).
export function templateRowScope(templateId: string | null): RowScope | null {
  if (templateId === null) return null;
  return Object.prototype.hasOwnProperty.call(TEMPLATE_ROW_SCOPE, templateId) ? TEMPLATE_ROW_SCOPE[templateId] : null;
}
